import React, { useEffect, useState } from 'react'
import { Button, Spinner, Typography } from '@telavita-core/react-design-kit'
import { formatISO, isEqual } from 'date-fns'

import { Reservation } from 'models/Reservation'
import { SelectNewReservationDate } from './components/SelectNewReservationDate'
import { compareAndReplaceDates } from './utils/compareAndReplaceDates'

import { useGetProfessionalAvailabilities, useUpdateReservation } from 'hooks'
import { useGetNextReservationEvents } from '../../hooks/useGetNextReservationEvents'

import * as S from './styles'

interface ReservationDate {
  oldDate: Date
  newDate: Date
}

interface RescheduleRecurrentReservationContentProps {
  reservation: Reservation
  handleSuccess: (newReservationDate: ReservationDate[]) => void
}

const QUANTITY_OF_DATES_TO_SHOW = 3

export const RescheduleRecurrentReservationContent: 
  React.FC<RescheduleRecurrentReservationContentProps> = ({ 
    reservation, 
    handleSuccess 
  }) => {
    const [visibleCount, setVisibleCount] = useState(QUANTITY_OF_DATES_TO_SHOW)
    const [selectedDatesForNewReservation, setSelectedDatesForNewReservation] = useState<ReservationDate[]>([])
    
    const fulfilledFields = selectedDatesForNewReservation.every(item => item.newDate !== null)
    const validSelectedDates = selectedDatesForNewReservation.some(item => !isEqual(item.oldDate, item.newDate))
    const enableButton = fulfilledFields && validSelectedDates

    const updateReservation = useUpdateReservation()
    const { nextReservationEventsData, isLoading } = useGetNextReservationEvents(reservation.uuid)
    const { data: professionalAvailabilitiesData, refetch } = useGetProfessionalAvailabilities({
      patientUsername: reservation.patientUsername,
      professionalUsername: reservation.professionalUsername,
      restrictions: nextReservationEventsData
    })

    useEffect(() => {
      if (nextReservationEventsData.length > 0) {
        refetch()
      }
    }, [nextReservationEventsData.length, refetch])
  
    const handleShowMore = () => {
      setVisibleCount((prevCount) => prevCount + QUANTITY_OF_DATES_TO_SHOW)
    }
  
    const handleSelectDate = (selectedDate: ReservationDate, index: number) => {
      const newArray = selectedDatesForNewReservation
      newArray[index] = selectedDate

      setSelectedDatesForNewReservation([...newArray])
    }

    const reservationDateToUpdate = selectedDatesForNewReservation.filter(item => !isEqual(item.oldDate, item.newDate))

    const handleUpdateReservation = () => {
      const formatDatesToIso = reservationDateToUpdate.map(item => ({
        oldDate: formatISO(item.oldDate),
        newDate: formatISO(item.newDate)
      }))

      updateReservation.mutate({
        uuid: reservation.uuid,
        nonRecurrentReservationDates: formatDatesToIso,
        professionalUsername: reservation.professionalUsername,
        patientUsername: reservation.patientUsername
      })
    }

    useEffect(() => {
      if (updateReservation.isSuccess) {
        handleSuccess(reservationDateToUpdate)
      }
    }, [updateReservation.isSuccess, handleSuccess, reservationDateToUpdate])

    if (isLoading || !professionalAvailabilitiesData?.length) {
      return (
        <S.SpinnerWrapper>
          <Spinner />
        </S.SpinnerWrapper>
      )
    }

    return (
      <S.Container>
        <S.TextWrapper>
          <Typography variant="content2" center>
            <span>Importante:</span> tente remarcar a reserva para o mesmo dia. 
            Caso não seja possível, você só poderá alterar o dia dentro da mesma semana.
          </Typography>
        </S.TextWrapper>

        <S.DateWrapper>
          {
            professionalAvailabilitiesData?.length && 
            compareAndReplaceDates(professionalAvailabilitiesData, nextReservationEventsData)
              .slice(0, visibleCount).map((date, index) => {
                return (
                  <SelectNewReservationDate
                    key={Object.keys(date)[0]}
                    date={date}
                    handleSelectDate={(selectedDate) => handleSelectDate(selectedDate, index)}
                    reservation={reservation}
                  />
                )
              })
          }
          <Button 
            uppercase={false} 
            variant='text' 
            onClick={handleShowMore} 
            disabled={visibleCount >= professionalAvailabilitiesData?.length}
          >
          Ver mais
          </Button>
        </S.DateWrapper>

        <Button 
          onClick={handleUpdateReservation} 
          variant='contained'
          disabled={!enableButton || updateReservation.isLoading}
        >
          {
            updateReservation.isLoading
              ? <Spinner customClassName='customSpinner'/>
              : 'Confirmar remarcação'
          }
        </Button>
      </S.Container>
    )
  }